import { useMemo } from 'react';
import { useTheme } from '@mui/material';

import type {
  ApiDashboardAggregate,
  CrossSurveyAggregate,
  DiagramType,
  UpstreamApiCrossSurveyBenchmarkData,
} from 'src/types/dashboard';
import { isNumber, roundOrNull } from 'src/helper/helper';

interface UseSpeedometerReDataParams {
  diagram?: DiagramType;
  focalAggregate?: ApiDashboardAggregate | null;
  comparisonAggregate?: ApiDashboardAggregate | null;
  benchmarkData?: UpstreamApiCrossSurveyBenchmarkData;
  benchmarkQuestion?: string;
}

interface SpeedometerReData {
  totalValue: number | null;
  yoyDiffAttribute: number | null;
  comparisonAttribute: number | null;
  benchmarkResultForWidget: CrossSurveyAggregate | undefined;
  benchmarkValue: number | null;
  benchmarkColor: string;
  benchmarkPercentage: number | null;
}

export const useSpeedometerReData = ({
  diagram,
  focalAggregate,
  comparisonAggregate,
  benchmarkData,
  benchmarkQuestion,
}: UseSpeedometerReDataParams): SpeedometerReData => {
  const theme = useTheme();

  const selectedAttributeIsPtop2 =
    diagram?.attribute && diagram.attribute === 'ptop2';

  const totalValue = useMemo(() => {
    const result = selectedAttributeIsPtop2
      ? focalAggregate?.ptop2
      : focalAggregate?.mean;
    if (typeof result === 'number') {
      return Math.round(result);
    }
    return null;
  }, [focalAggregate, selectedAttributeIsPtop2]);

  const yoyDiffAttribute = useMemo(
    () =>
      (selectedAttributeIsPtop2
        ? focalAggregate?.ptop2_yoyDiff
        : focalAggregate?.mean_yoyDiff) ?? null,
    [focalAggregate, selectedAttributeIsPtop2],
  );

  const comparisonAttribute = useMemo(
    () =>
      (selectedAttributeIsPtop2
        ? comparisonAggregate?.ptop2
        : comparisonAggregate?.mean) ?? null,
    [comparisonAggregate, selectedAttributeIsPtop2],
  );

  // cross-survey benchmark result
  const benchmarkResultForWidget = useMemo(() => {
    if (
      benchmarkQuestion &&
      benchmarkData?.results &&
      benchmarkData?.results.length > 0
    ) {
      return benchmarkData.results
        .flatMap((result) => result.aggregates)
        .find((aggregate) => aggregate.question == benchmarkQuestion);
    }
  }, [benchmarkQuestion, benchmarkData]);

  const benchmarkValue =
    (selectedAttributeIsPtop2
      ? roundOrNull(benchmarkResultForWidget?.ptop2)
      : roundOrNull(benchmarkResultForWidget?.mean)) ?? null;

  const benchmarkColor = diagram?.benchmark_color ?? theme.colors.black;

  const benchmarkPercentage = isNumber(benchmarkValue)
    ? Number(benchmarkValue) / 100
    : null;

  return {
    totalValue,
    yoyDiffAttribute,
    comparisonAttribute,
    benchmarkResultForWidget,
    benchmarkValue,
    benchmarkColor,
    benchmarkPercentage,
  };
};
